import { FC, ReactNode } from 'react';
import { Box, Typography } from '@robocorp/components';
import { styled } from '@robocorp/theme';
import { IconFile } from '@robocorp/icons/iconic';

type Props = {
  children?: ReactNode;
  fileName: ReactNode;
  fileSize: ReactNode;
  onClick: () => void;
  active?: boolean;
};

const Container = styled.li`
  display: flex;
  border-radius: ${({ theme }) => theme.radii.$12};
  padding: ${({ theme }) => theme.space.$4};
  background: ${({ theme }) => theme.colors.background.subtle.color};

  &:hover {
    background: ${({ theme }) => theme.colors.background.subtle.hovered.color};
  }

  &[aria-current='true'] {
    background: ${({ theme }) => theme.colors.background.accent.light.color};
  }
`;

const Button = styled.button`
  flex: 1;
  min-width: 0;
  border-radius: ${({ theme }) => theme.radii.$12} 0 0 ${({ theme }) => theme.radii.$12};
  display: flex;
  align-items: center;
  background: none;
  text-align: left;
  word-break: break-all;
`;

/**
 * FileList helper component to display a single file in a list with optional actions that can be passed as children
 *
 * @example
 * <Filelist.File onClick={onClick} fileName="Foo.bar" fileSize="12Kb">
 *   <Button icon={IconTrash} onClick={onRemove} variant="ghost" aria-label="delete" />
 * <Filelist.File>
 */
export const File: FC<Props> = ({ active, fileName, fileSize, children, onClick }) => {
  return (
    <Container aria-current={active}>
      <Button type="button" onClick={onClick}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width={40}
          minWidth={40}
          height={40}
          mr="$12"
          backgroundColor="background.accent.light"
          borderRadius="$12"
        >
          <IconFile color="content.primary" />
        </Box>
        <Box>
          <Typography
            truncate={1}
            variant="body.medium"
            fontWeight="medium"
            color="content.primary"
          >
            {fileName}
          </Typography>
          <Typography fontSize="$11" variant="body.small" color="content.subtle.light">
            {fileSize}
          </Typography>
        </Box>
      </Button>
      {children}
    </Container>
  );
};
